// src/components/ui/Button.js
import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.size === 'small'
        ? '0.5rem 1rem'
        : props.size === 'large'
            ? '1rem 2.5rem'
            : '0.75rem 1.5rem'};
  font-size: ${props => props.size === 'small'
        ? props.theme.fontSizes.small
        : props.size === 'large'
            ? props.theme.fontSizes.large
            : props.theme.fontSizes.medium};
  font-weight: 600;
  border-radius: ${props => props.theme.borderRadius.medium};
  transition: all ${props => props.theme.transitions.fast};
  cursor: pointer;
  text-decoration: none;
  border: none;
  font-family: ${props => props.theme.fonts.main};
  
  ${props => props.fullWidth && css`
    width: 100%;
  `}
  
  /* Variants */
  ${props => props.variant === 'primary' && css`
    background-color: ${props.theme.colors.primary};
    color: ${props.theme.colors.light};
    
    &:hover, &:focus {
      background-color: ${props.theme.colors.primaryDark};
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  ${props => props.variant === 'secondary' && css`
    background-color: transparent;
    color: ${props.theme.colors.primary};
    border: 2px solid ${props.theme.colors.primary};
    
    &:hover, &:focus {
      background-color: ${props.theme.colors.primary};
      color: ${props.theme.colors.light};
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  ${props => props.variant === 'text' && css`
    background-color: transparent;
    color: ${props.theme.colors.primary};
    padding-left: 0;
    padding-right: 0;
    
    &:hover, &:focus {
      color: ${props.theme.colors.primaryDark};
      background-color: transparent;
    }
    
    svg {
      margin-left: 0.5rem;
      transition: transform ${props.theme.transitions.fast};
    }
    
    &:hover svg {
      transform: translateX(5px);
    }
  `}
  
  ${props => props.variant === 'light' && css`
    background-color: ${props.theme.colors.light};
    color: ${props.theme.colors.primary};
    
    &:hover, &:focus {
      background-color: rgba(255, 255, 255, 0.9);
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  /* Disabled state */
  ${props => props.disabled && css`
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
    
    &:hover, &:focus {
      background-color: ${props.variant === 'primary'
            ? props.theme.colors.primary
            : props.variant === 'secondary'
                ? 'transparent'
                : props.variant === 'light'
                    ? props.theme.colors.light
                    : 'transparent'};
      color: ${props.variant === 'secondary' || props.variant === 'text'
            ? props.theme.colors.primary
            : props.variant === 'light'
                ? props.theme.colors.primary
                : props.theme.colors.light};
    }
  `}
`;

const StyledButton = styled.button`
  ${buttonStyles}
`;

const StyledLink = styled(Link)`
  ${buttonStyles}
`;

const StyledExternalLink = styled.a`
  ${buttonStyles}
`;

const Button = ({
    children,
    variant = 'primary',
    size = 'medium',
    to,
    href,
    onClick,
    disabled,
    type = 'button',
    fullWidth,
    ...rest
}) => {
    // For internal links
    if (to) {
        return (
            <StyledLink
                to={to}
                variant={variant}
                size={size}
                disabled={disabled}
                fullWidth={fullWidth}
                {...rest}
            >
                {children}
            </StyledLink>
        );
    }

    // For external links
    if (href) {
        return (
            <StyledExternalLink
                href={href}
                variant={variant}
                size={size}
                disabled={disabled}
                fullWidth={fullWidth}
                target="_blank"
                rel="noopener noreferrer"
                {...rest}
            >
                {children}
            </StyledExternalLink>
        );
    }

    // For buttons
    return (
        <StyledButton
            type={type}
            variant={variant}
            size={size}
            onClick={onClick}
            disabled={disabled}
            fullWidth={fullWidth}
            {...rest}
        >
            {children}
        </StyledButton>
    );
};

export default Button;